/**
 * Interpretação das respostas de `POST /api/pipeline/orders/<id>/reopen`.
 *
 * POR QUE ISTO É UM MÓDULO SEPARADO
 *
 *   A reversão tem cinco desfechos possíveis e dois deles exigem reação
 *   diferente da tela: 404 e 409-já-revertida significam que o card está
 *   DESATUALIZADO em relação ao banco, e a única saída correta é recarregar;
 *   já o 409 de conflito significa que o banco está certo e o usuário
 *   precisa entender o que mudou antes de decidir.
 *
 *   Deixar isso como uma cadeia de `if` dentro do componente tornaria a
 *   regra invisível e não verificável. Aqui ela é pura e tem teste.
 *
 * O componente cuida de UI e interação. Este módulo cuida de traduzir a
 * resposta do servidor em: o que dizer, o que listar, e se recarrega.
 */

export const MENSAGENS = {
  motivo: 'Informe o motivo da reversão.',
  semSnapshot:
    'Esta oportunidade foi fechada antes do registro de reversão. ' +
    'Não é possível desfazer automaticamente.',
  jaRevertida: 'Este fechamento já foi revertido.',
  conflito:
    'Esta oportunidade foi alterada depois do fechamento. ' +
    'A reversão foi interrompida para não apagar essas alterações.',
  generico: 'Não foi possível desfazer. Tente novamente.',
};

const curto = (id) => (id ? String(id).slice(0, 8) : '');

/**
 * Descreve um conflito em linguagem de gente.
 *
 * O backend devolve `{tipo, id, esperado, atual}`. Mostrar JSON cru para um
 * vendedor não ajuda ninguém a decidir nada.
 */
export function descreverConflito(conflito) {
  const c = conflito || {};
  switch (c.tipo) {
    case 'cotacao_ausente':
      return `A cotação ${curto(c.id)} foi excluída depois do fechamento.`;
    case 'cotacao_alterada':
      return `A cotação ${curto(c.id)} foi alterada depois do fechamento.`;
    case 'order_alterado':
      return 'A oportunidade foi alterada depois do fechamento.';
    default:
      return 'Uma alteração posterior ao fechamento foi detectada.';
  }
}

/**
 * Traduz `(status, corpo)` no desfecho que a tela precisa.
 *
 * @returns {{ok: boolean, mensagem: string|null, conflitos: Array,
 *            avisos: Array, recarregar: boolean}}
 *
 * `recarregar` é o campo que importa: true quando a tela está mostrando
 * algo que o banco já não confirma, e insistir seria enganar o usuário.
 */
export function interpretarCorpo(status, corpo) {
  const c = corpo || {};
  const base = { ok: false, mensagem: null, conflitos: [], avisos: [], recarregar: false };

  if (status === 200 && c.success) {
    return { ...base, ok: true, avisos: c.avisos || [] };
  }

  if (status === 400) {
    return { ...base, mensagem: c.error || MENSAGENS.motivo };
  }

  if (status === 404) {
    // A oportunidade não tem fechamento reversível — o botão não devia
    // estar ali. Recarregar faz ele sumir.
    return { ...base, mensagem: c.error || MENSAGENS.semSnapshot, recarregar: true };
  }

  if (status === 409) {
    if (c.error === 'conflito_de_reversao') {
      // NÃO recarrega: o card está correto, quem mudou foi outra coisa.
      // O usuário precisa ler a lista antes de decidir.
      return {
        ...base,
        mensagem: c.message || MENSAGENS.conflito,
        conflitos: c.conflitos || [],
      };
    }
    // Já revertida — alguém (ou o próprio duplo clique) chegou antes.
    return { ...base, mensagem: c.error || MENSAGENS.jaRevertida, recarregar: true };
  }

  return { ...base, mensagem: c.error || MENSAGENS.generico };
}

/**
 * Mesmo que `interpretarCorpo`, partindo de um `Response` do fetch.
 * Corpo não-JSON ou vazio cai no genérico em vez de explodir.
 */
export async function interpretarReversao(resp) {
  if (!resp) {
    return { ok: false, mensagem: MENSAGENS.generico, conflitos: [], avisos: [], recarregar: false };
  }
  let corpo = null;
  try {
    corpo = await resp.json();
  } catch (e) {
    corpo = null;
  }
  return interpretarCorpo(resp.status, corpo);
}

export default interpretarReversao;
